import React, { useState, useEffect } from 'react';
import { 
  Snackbar, 
  Alert, 
  Typography,
  Box
} from '@mui/material';
import { 
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon
} from '@mui/icons-material';

/**
 * @component OfflineIndicator
 * @description Component that shows a notification when the user goes offline or comes back online
 */
const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineMessage, setShowOfflineMessage] = useState(false);
  const [showOnlineMessage, setShowOnlineMessage] = useState(false);

  useEffect(() => {
    // Function to handle online status change
    const handleOnlineStatusChange = () => {
      const online = navigator.onLine;
      
      if (online && !isOnline) {
        // User came back online
        setShowOnlineMessage(true);
        setShowOfflineMessage(false);
      } else if (!online && isOnline) {
        // User went offline
        setShowOfflineMessage(true);
        setShowOnlineMessage(false);
      }
      
      setIsOnline(online);
    };

    // Add event listeners
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    // Initial check
    handleOnlineStatusChange();

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, [isOnline]);

  const handleCloseOfflineMessage = () => {
    setShowOfflineMessage(false);
  };

  const handleCloseOnlineMessage = () => {
    setShowOnlineMessage(false);
  };

  return (
    <>
      {/* Offline Alert */}
      <Snackbar
        open={showOfflineMessage}
        autoHideDuration={6000}
        onClose={handleCloseOfflineMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="warning" 
          onClose={handleCloseOfflineMessage}
          icon={<WifiOffIcon />}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            You are currently offline. Some features may be limited.
          </Typography>
        </Alert>
      </Snackbar>

      {/* Online Alert */}
      <Snackbar
        open={showOnlineMessage}
        autoHideDuration={3000}
        onClose={handleCloseOnlineMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          onClose={handleCloseOnlineMessage}
          icon={<WifiIcon />}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            You're back online!
          </Typography>
        </Alert>
      </Snackbar>

      {/* Persistent Offline Indicator */}
      {!isOnline && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'warning.dark',
            color: 'warning.contrastText',
            py: 0.5,
            textAlign: 'center',
            zIndex: 9999
          }}
        >
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WifiOffIcon fontSize="small" sx={{ mr: 0.5 }} />
            Offline Mode - Limited functionality available
          </Typography>
        </Box>
      )}
    </>
  );
};

export default OfflineIndicator;
